import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import validate from '../../helpers/Validate';
import { stateAbbreviation } from '../../helpers/Address';
import { renderDate } from '../../helpers/Date';
import Supporters from '../../api/Supporters';
import { ModalButtonGroup, ModalCancelButton, ModalSaveButton } from '../Modals';
import { Text, Address, Phone } from '../Forms';

const AddressField = ({ address, form, type }) => {
  /* eslint-disable camelcase */
  const { address_line_1, address_line_2, suburb, city, zip, state, country, modified } = address;

  const label = `${type[0].toUpperCase()}${type.slice(1)} Address`;

  useEffect(() => {
    form.setValue(`${type}Address`, {
      display: [address_line_1, address_line_2, suburb, city, zip, stateAbbreviation(state), country].filter((el) => el !== undefined && el !== '').join(', '),
      type,
      address_line_1,
      address_line_2,
      suburb,
      city,
      zip,
      state: stateAbbreviation(state),
      country,
    });
    form.setValue(`${type}_address_line_1`, address_line_1);
    form.setValue(`${type}_address_line_2`, address_line_2);
    form.setValue(`${type}_suburb`, suburb);
    form.setValue(`${type}_city`, city);
    form.setValue(`${type}_zip`, zip);
    form.setValue(`${type}_state`, state);
    form.setValue(`${type}_country`, country);
  }, []);

  const [enterAddressManually, setEnterAddressManually] = useState(false);

  const suggestChanged = (newAddress) => {
    Object.keys(newAddress).map((key) => {
      if (key !== 'display') {
        if (key === 'address_line_2') {
          // the google auto complete address has no suburb
          // but address_line_2 is sublocality_level_1 which
          // is the same thing...
          form.setValue(`${type}_suburb`, newAddress.address_line_2);
          form.setTouched(`${type}_suburb`);
        } else {
          form.setValue(`${type}_${key}`, newAddress[key]);
          form.setTouched(`${type}_${key}`);
        }
      }
    });
    const updatedAddress = { ...newAddress };
    updatedAddress.suburb = newAddress.address_line_2;
    form.setValue(`${type}Address`, updatedAddress);
  };

  const updateManualAddress = (e) => {
    const newAddress = form.getValue(`${type}Address`);
    const updatedValue = form.getValue(e.target.id);
    form.setTouched(e.target.id);
    newAddress[e.target.id.replace(`${type}_`, '')] = updatedValue;
    newAddress.display = [
      newAddress.address_line_1,
      newAddress.address_line_2,
      newAddress.suburb,
      newAddress.city,
      newAddress.zip,
      stateAbbreviation(newAddress.state),
      newAddress.country,
    ]
      .filter((el) => el !== undefined && el !== '')
      .join(', ');
    form.setValue(`${type}Address`, newAddress);
  };

  const toggleAddressEntry = () => setEnterAddressManually(!enterAddressManually);

  return (
    <Fragment>
      <div className="label-with-meta">
        <label htmlFor={`${type}Address`}>{label}</label>
        {modified && <small className="meta">Updated: {renderDate(modified)}</small>}
      </div>
      <div className={!enterAddressManually ? 'form__row' : 'form__row hidden'}>
        <Address id={`${type}Address`} field={`${type}Address`} textNoResults={null} onSelectSuggest={suggestChanged} />
        <p className="contact-details-modal__description">
          Having trouble finding your address?{' '}
          <button type="button" onClick={toggleAddressEntry}>
            You can enter your address manually.
          </button>
        </p>
      </div>
      <div className={enterAddressManually ? 'manual-address' : 'hidden'}>
        <div>
          <div className="form__row">
            <Text label="Address Line 1" id={`${type}_address_line_1`} field={`${type}_address_line_1`} autoComplete="off" onBlur={updateManualAddress} />
          </div>{' '}
          <div className="form__row">
            <Text label="Address Line 2" id={`${type}_address_line_2`} field={`${type}_address_line_2`} autoComplete="off" onBlur={updateManualAddress} />
          </div>
          <div className="form__row">
            <Text label="Suburb" id={`${type}_suburb`} field={`${type}_suburb`} autoComplete="off" onBlur={updateManualAddress} />
          </div>
          <div className="form__row">
            <Text label="City" id={`${type}_city`} field={`${type}_city`} autoComplete="off" onBlur={updateManualAddress} />
            <Text label="Postcode/Zip Code" id={`${type}_zip`} field={`${type}_zip`} autoComplete="off" onBlur={updateManualAddress} />
          </div>
          <div className="form__row">
            <Text label="State" id={`${type}_state`} field={`${type}_state`} autoComplete="off" onBlur={updateManualAddress} />
            <Text label="Country" id={`${type}_country`} field={`${type}_country`} autoComplete="off" onBlur={updateManualAddress} />
          </div>
        </div>
        <p className="contact-details-modal__description">
          <button type="button" onClick={toggleAddressEntry}>
            Search for your address.
          </button>
        </p>
      </div>
    </Fragment>
  );
};

AddressField.propTypes = {
  type: PropTypes.string.isRequired,
  address: PropTypes.objectOf(PropTypes.any).isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
};

const SupporterDetailsForm = ({
  contactID,
  fullname,
  emailAddress,
  streetAddress,
  postalAddress,
  mobile,
  mobileModified,
  phone,
  phoneModified,
  onClose,
  contacts,
  setContactDetails,
  setContacts,
}) => {
  const [buttonType, setButtonType] = useState('');

  const validateErrorRules = {
    phone: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a phone number',
      },
    },
    streetAddress: {
      presence: {
        allowEmpty: false,
        message: 'Please enter an address',
      },
    },
    street_country: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a country',
      },
    },
    street_zip: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a postcode/zip code',
      },
    },
    street_address_line_1: {
      presence: {
        allowEmpty: false,
        message: 'Please enter an address',
      },
    },
    street_state: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a state',
      },
    },
    street_city: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a city',
      },
    },
    postalAddress: {
      presence: {
        allowEmpty: false,
        message: 'Please enter an address',
      },
    },
    postal_country: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a country',
      },
    },
    postal_zip: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a postcode/zip code',
      },
    },
    postal_address_line_1: {
      presence: {
        allowEmpty: false,
        message: 'Please enter an address',
      },
    },
    postal_state: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a state',
      },
    },
    postal_city: {
      presence: {
        allowEmpty: false,
        message: 'Please enter a city',
      },
    },
  };

  return (
    null
    // <Form
    //   defaultValues={{ streetAddress, postalAddress, phone, mobile }}
    //   validateError={(values) => {
    //     const formValues = JSON.parse(JSON.stringify(values));
    //     formValues.streetAddress = formValues.streetAddress.display;
    //     formValues.postalAddress = formValues.postalAddress.display;
    //     return validate(formValues, validateErrorRules, { fullMessages: false });
    //   }}
    //   render={(form) => {
    //     const handleSubmit = () => {
    //       // before we submit, we need to check which values have changed....

    //       const updatedValues = {};
    //       for (const [key, value] of Object.entries(form.values)) {
    //         if (form.getTouched(key) && (key === 'postalAddress' || key === 'streetAddress' || key === 'phone' || key === 'mobile')) {
    //           updatedValues[key] = value;
    //         }
    //       }

    //       // prevent empty submission...
    //       if (Object.keys(updatedValues).length === 0 && updatedValues.constructor === Object) {
    //         return false;
    //       }

    //       setButtonType('saving');

    //       Supporters.save({ contact_id: contactID, ...updatedValues })
    //         .then((response) => {
    //           if (response.result !== 'success') {
    //             setButtonType('error');
    //             throw new Error(response.error);
    //           }
    //           const storedContactIndex = contacts.indexOf(contacts.find((contact) => contact.contact_id === contactID));
    //           const updatedContacts = [...contacts];
    //           const justNow = Math.floor(Date.now() / 1000);
    //           updatedContacts[storedContactIndex] = {
    //             contact_id: contactID,
    //             fullname,
    //             email: emailAddress,
    //             phone: updatedValues.phone || updatedContacts[storedContactIndex].phone,
    //             mobile: updatedValues.mobile || updatedContacts[storedContactIndex].mobile,
    //             postalAddress: updatedValues.postalAddress
    //               ? { ...updatedValues.postalAddress, modified: justNow }
    //               : updatedContacts[storedContactIndex].postalAddress,
    //             streetAddress: updatedValues.streetAddress
    //               ? { ...updatedValues.streetAddress, modified: justNow }
    //               : updatedContacts[storedContactIndex].streetAddress,
    //             mobile_modified: updatedValues.mobile ? justNow : updatedContacts[storedContactIndex].mobile_modified,
    //             phone_modified: updatedValues.phone ? justNow : updatedContacts[storedContactIndex].phone_modified,
    //           };
    //           setContactDetails(updatedContacts[storedContactIndex]);
    //           setContacts(updatedContacts);
    //           setButtonType('success');
    //           setTimeout(() => onClose(), 1000);
    //         })
    //         .catch((e) => {
    //           setButtonType('error');
    //           throw new Error(e);
    //         });
    //     };

    //     return (
    //       <form className="form" autoComplete="off" onSubmit={handleSubmit}>
    //         <div className="contact-details-modal__content">
    //           <h3 className="contact-details-modal__name">{fullname}</h3>
    //           <hr />
    //           <AddressField address={streetAddress} form={form} type="street" />
    //           <AddressField address={postalAddress} form={form} type="postal" />
    //           <div className="form__row">
    //             <Phone
    //               onChange={(value) => form.setValue('mobile', value)}
    //               value={form.getValue('mobile')}
    //               label="Mobile Number"
    //               id="mobile"
    //               field="mobile"
    //               className="flex-row__auto form__phone-number"
    //               modified={mobileModified}
    //             />
    //           </div>
    //           <div className="form__row">
    //             <Phone
    //               onChange={(value) => form.setValue('phone', value)}
    //               value={form.getValue('mobile')}
    //               label="Phone Number"
    //               id="phone"
    //               field="phone"
    //               className="flex-row__auto form__phone-number"
    //               modified={phoneModified}
    //             />
    //           </div>
    //           <div className="form__row">
    //             <div className="form__text">
    //               <label>Email Address</label>
    //               <div className="relative">
    //                 <input className="form__email" type="text" disabled value={emailAddress} />
    //               </div>
    //             </div>
    //             <p className="contact-details-modal__description">
    //               To update a supporter&apos;s email address, please contact <a href="mailto:support@afes.org.au">support@afes.org.au</a>
    //             </p>
    //           </div>
    //         </div>
    //         <ModalButtonGroup>
    //           <ModalCancelButton onClick={onClose}>Close</ModalCancelButton>
    //           <ModalSaveButton type={buttonType} onClick={handleSubmit}>
    //             Save Changes
    //           </ModalSaveButton>
    //         </ModalButtonGroup>
    //       </form>
    //     );
    //   }}
    // />
  );
};

SupporterDetailsForm.defaultProps = {
  fullname: '',
  emailAddress: '',
  streetAddress: {},
  postalAddress: {},
  mobile: '',
  mobileModified: null,
  phone: '',
  phoneModified: null,
  contacts: [],
};

SupporterDetailsForm.propTypes = {
  contactID: PropTypes.number.isRequired,
  fullname: PropTypes.string,
  emailAddress: PropTypes.string,
  streetAddress: PropTypes.objectOf(PropTypes.any),
  postalAddress: PropTypes.objectOf(PropTypes.any),
  mobile: PropTypes.string,
  mobileModified: PropTypes.number,
  phone: PropTypes.string,
  phoneModified: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  contacts: PropTypes.arrayOf(PropTypes.any),
  setContactDetails: PropTypes.func.isRequired,
  setContacts: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  setContacts: (contacts) => dispatch({ type: 'SET_CONTACTS', contacts }),
  setContactDetails: (contactDetails) => dispatch({ type: 'SET_CONTACT_MODAL_DETAILS', contactDetails }),
});

const mapStateToProps = (state) => ({
  contacts: state.supporters.contacts,
  contactID: state.supporters.contactModal.contactID,
  fullname: state.supporters.contactModal.contactDetails.fullname,
  streetAddress: state.supporters.contactModal.contactDetails.streetAddress,
  postalAddress: state.supporters.contactModal.contactDetails.postalAddress,
  mobile: state.supporters.contactModal.contactDetails.mobile,
  phone: state.supporters.contactModal.contactDetails.phone,
  mobileModified: state.supporters.contactModal.contactDetails.mobile_modified,
  phoneModified: state.supporters.contactModal.contactDetails.phone_modified,
  emailAddress: state.supporters.contactModal.contactDetails.email,
});

export default connect(mapStateToProps, mapDispatchToProps)(SupporterDetailsForm);
